'use client';

import type { PropsWithChildren } from 'react';
import type { Collection } from '@/app/actions/collection';
import type { User } from '@/types/user/type';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import type { ContributionType } from '~/model/ContributionType';
import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import type { UserInteractionStatusType } from '~/model/InteractionType';
import type { QuestionReconstructedType } from '~/model/QuestionType';
import type { ResultsType } from '~/model/ResultsType';
import type { SportType } from '~/model/SportType';
import type { SelectedTimeSlotType, TimeSlotType } from '~/model/TimeSlotType';
import { VpButton, VpFormControl, VpFormLabel, VpIcon, VpRadioGroup, VpRadioGroupItem } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { fetchPendingCriteria, fetchTimeslots, getChildContribution, getResultsData } from '@/app/actions/server';
import { ChidrenFormAddBtn } from '@/app/components/ChidrenFormAddBtn';
import { MainColumn } from '@/app/components/MainColumn';
import { useProgressContext } from '@/app/components/Provider';
import { useSlideAnimationContext } from '@/app/components/Provider/SlideAnimationProvider';
import { cannotInitializeAgain } from '@/app/utils/contributions/initialize';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { journeyColumnTemplate } from '~/components/Journey/config';
import appConst from '~/const/appConst';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';
import { CriteriaWrapper } from './CriteriaWrapper';
import { useFormContext } from './FormProvider';
import { ContributorProvider } from './provider/ContributorProvider';
import { ProfileCompletionProvider } from './provider/ProfileCompletionProvider';
import { TimeslotsProvider } from './provider/TimeslotsProvider';
import { Results } from './Results';
import { ResultsNavigation } from './Results/ResultsNavigation';
import { SurveyDefaultConclusion } from './Survey/SurveyDefaultConclusion';

export const ChildSeletor = ({
  children,
  childList,
}: PropsWithChildren<{
  childList: Collection<User>;
}>) => {
  const t = useTranslations();

  const { conclusions, sections, questions, interaction, setInteraction, setQuestions } = useFormContext();
  const { total, setIndex } = useProgressContext();
  const { animateSlide } = useSlideAnimationContext();
  const { startRequest, handleSuccess, status: requestStatus } = useRequestState();

  const [comments, setComments] = useState<Array<{
    [x: number]: Collection<AnswerType & CommentType>;
  }>>();

  const [results, setResults] = useState<ResultsType>();
  const [selectedChild, setSelectedChild] = useState<User>();
  const [contributor, setContributor] = useState<User>();
  const [contributions, setContributions] = useState<Collection<ContributionType>>();
  const [answers, setAnswers] = useState<Collection<AnswerType>>();
  const [status, setStatus] = useState<UserInteractionStatusType>();
  const [timeslots, setTimeslots] = useState<Collection<TimeSlotType>>();
  const [selectedTimeslots, setSelectedTimeslots] = useState<Collection<SelectedTimeSlotType>>();
  const [pendingCriteria, setPendingCriteria] = useState<{
    sports: Collection<SportType>;
    requestedCriteria: Collection<CriteriaType>;
    requestedCriteriaChoiceUser: Collection<CriteriaChoiceUserType>;
    mandatoryCriteria: Collection<CriteriaType>;
    mandatoryCriteriaChoiceUser: Collection<CriteriaChoiceUserType>;
    sportsCriterion: CriteriaType[];
  }>();

  const isSurvey = interaction['@type'] === appConst.entityTypes.survey;
  const isPending = requestStatus === RequestStatus.PENDING;
  const id = interaction.id;

  const handleSubmit = async () => {
    startRequest();

    const {
      interaction,
      status,
      contributionId,
      contributions,
      answers,
    } = await getChildContribution(id, selectedChild.id, selectedChild['@id']);

    const contributorStatus = interaction.current_user_interaction_status;
    const statusCount = interaction.current_user_contribution.count_per_status;
    const checkCannotInitializeAgain = cannotInitializeAgain(interaction, statusCount);

    if (isSurvey && checkCannotInitializeAgain) {
      const { questions, comments, results } = await getResultsData(interaction.id);

      return await animateSlide({
        callback: async () => {
          setIndex(total);
          setComments(comments);
          setResults(results);
          setInteraction(interaction);
          setQuestions(questions);
          setContributor(selectedChild);
          handleSuccess();
        },
      });
    }

    const {
      mandatoryCriteria,
      mandatoryCriteriaChoiceUser,
      requestedCriteria,
      requestedCriteriaChoiceUser,
      sports,
      sportsCriterion,
    } = await fetchPendingCriteria({
      criterionStatus: contributorStatus.criterion_status,
      created_for: selectedChild.id,
    });

    const {
      timeslots,
      selectedTimeslots,
    } = !!interaction.time_slots?.length && await fetchTimeslots(
      interaction.id,
      checkCannotInitializeAgain && contributionId,
    );

    setTimeslots(timeslots);
    setSelectedTimeslots(selectedTimeslots);
    setPendingCriteria({
      requestedCriteria,
      mandatoryCriteria,
      mandatoryCriteriaChoiceUser,
      requestedCriteriaChoiceUser,
      sports,
      sportsCriterion,
    });

    setStatus(status);
    setAnswers(answers);
    setContributions(contributions);
    setInteraction(interaction);

    await animateSlide({
      callback: async () => {
        setIndex(current => checkCannotInitializeAgain ? total : current + 1);
        setContributor(selectedChild);
        handleSuccess();
      },
    });
  };

  if (results) {
    return interaction.result_visible_on_front
      ? (
          <Results
            {...answers && { answers: answers['hydra:member'] }}
            conclusions={conclusions['hydra:member']}
            interaction={interaction}
            questions={questions as Array<QuestionReconstructedType>}
            results={results}
            sections={sections}
            comments={comments}
          >
            <ResultsNavigation id={interaction.id} />
          </Results>
        )
      : <SurveyDefaultConclusion interaction={interaction} />;
  }

  if (contributor) {
    const {
      requestedCriteria,
      requestedCriteriaChoiceUser,
      mandatoryCriteria,
      mandatoryCriteriaChoiceUser,
      sportsCriterion,
      sports,
    } = pendingCriteria;

    return (
      <ContributorProvider
        contributor={contributor}
        answers={answers}
        contributions={contributions}
        status={status}
      >
        <TimeslotsProvider
          timeslots={timeslots?.['hydra:member']}
          selectedTimeslots={selectedTimeslots?.['hydra:member']}
        >
          <ProfileCompletionProvider
            requestedCriteria={requestedCriteria['hydra:member']}
            requestedCriteriaChoiceUser={requestedCriteriaChoiceUser['hydra:member']}
            mandatoryCriteria={mandatoryCriteria['hydra:member']}
            mandatoryCriteriaChoiceUser={mandatoryCriteriaChoiceUser['hydra:member']}
            shouldUpdateSports={!!sportsCriterion.length}
          >
            <CriteriaWrapper interaction={interaction} sports={sports}>
              {children}
            </CriteriaWrapper>
          </ProfileCompletionProvider>
        </TimeslotsProvider>
      </ContributorProvider>
    );
  }

  return (
    <div className="children-form__inner__centered">
      <BeardmanTalking
        title={t('FORM.CHILDREN_HEADER')}
        description={t('FORM.CHILDREN_DESCRIPTION')}
      />
      <MainColumn column={journeyColumnTemplate}>
        <VpFormControl className="child-selector">
          <VpFormLabel>
            {t('FORM.CHILDREN_CHOICE')}
          </VpFormLabel>
          <VpRadioGroup
            className="children-form__radios"
            onValueChange={(event) => {
              if (event.currentTarget?.value) {
                setSelectedChild(childList['hydra:member'].find(child => child['@id'] === event.currentTarget.value));
              }
            }}
          >
            {childList['hydra:member']?.map(child => (
              <VpRadioGroupItem
                className="form__choice"
                key={child['@id']}
                data-testid="radio"
                data-cy="radio"
                id={child['@id']}
                value={child['@id']}
              >
                {child.firstname}
              </VpRadioGroupItem>
            ))}
            <ChidrenFormAddBtn />
          </VpRadioGroup>
        </VpFormControl>
        <VpButton
          className="children-form__next-button"
          data-cy="child-submit"
          data-testid="continueButton"
          disabled={!selectedChild || isPending}
          endSlot={<VpIcon name="arrow-right" />}
          onClick={handleSubmit}
          loading={isPending}
        >
          {t('GLOBAL.GO_ON')}
        </VpButton>
      </MainColumn>
    </div>
  );
};
